import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { Badge, Box, Card, Chip, Grid, Menu, MenuItem, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useChartResize from '../../../hooks/useChartResize';
import { getPredictiveDialerWidget } from '../../../services/api-service';
import { getLastFetchedDetailFromBrowser } from '../../../services/application-service';
import { ReactComponent as FilterAltOutlinedIcon } from '../../../static/icons/filter.svg';
import { WIDGET_REFRESH_INTERVAL } from '../../../utils/constants';
import { secondsToMinSec } from '../../../utils/util';
import FilterDrawerCaseWise from '../../FilterDrawer/FilterDrawerCaseWise';
import OZTable from '../../OZTable/OZTable';
import '../AgentPerformance/AgentPerformance.css';
import WidgetDownload from '../../Widget-Download/Widget-Download';

const DEFAULT_CASE_FILTERS = {
    caseCondition: 'AND',
    caseFilters: [{ condition: 'AND', conditionFilters: [{ filterType: '', filterSection: null, selectedFilters: [] }] }]
};

const TIME_FILTERS = [
    { label: 'Current Day', value: '0' },
    { label: 'Last 1 Hour', value: '1' },
];

const columns: any[] = [
    { name: 'Campaign Name', key: 'CampaignName', align: 'left', },
    // { name: 'Agent Logged in', key: 'AgentLoggedIn', align: 'left', },
    { name: 'Calls Dialled', key: 'CallsDialed', align: 'left', },
    { name: 'Answered', key: 'CallsAnswered', align: 'left', },
    { name: 'Average Queue Time', key: 'AverageQueueTime', align: 'left', },
    { name: 'Answered Abandoned', key: 'AnsweredAbandoned', align: 'left', },
    { name: 'Abandoned %', key: 'AbandonedPercentage', align: 'left', },
];

const normalizePercent = (value: any) => {
    if (value === null || value === undefined || value === '') {
        return '0%';
    }
    const stringValue = String(value);
    return stringValue.includes('%') ? stringValue : `${stringValue}%`;
};

const formatQueueTime = (value: any) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue) || numericValue <= 0) {
        return '00:00';
    }
    return secondsToMinSec(numericValue);
};

const normalizeRow = (row: any) => ({
    CampaignName: row?.CampaignName || '',
    AgentLoggedIn: row?.AgentLoggedIn ?? '0',
    CallsDialed: row?.CallsDialed ?? '0',
    CallsAnswered: row?.CallsAnswered ?? '0',
    AverageQueueTime: formatQueueTime(row?.AverageQueueTime),
    AnsweredAbandoned: row?.AnsweredAbandoned ?? '0',
    AbandonedPercentage: normalizePercent(row?.AbandonedPercentage),
});

const getTimeFilterValue = (value: any) => {
    if (value === 1 || value === '1') {
        return '1';
    }
    return '0';
};

const PredictiveDialerWidget: React.FC<any> = (props: any) => {
    const [lastFetchedTime, setLastFetchedTime] = useState<any>('');
    const [caseFilters, setCaseFilters] = useState<any>(props?.widgetData?.caseFilters || DEFAULT_CASE_FILTERS);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [rowData, setRowData] = useState<any[]>([]);
    const [timeFilter, setTimeFilter] = useState<string>(getTimeFilterValue(props?.widgetData?.timeFilter));
    const [timeMenuAnchorEl, setTimeMenuAnchorEl] = useState<null | HTMLElement>(null);
    const loadingRef = useRef(false);
    const intervalRef = useRef<any>(null);
    const downloadContent = useRef<null | any>(null);
    const { chartContainerRef, chartDimensions } = useChartResize();

    const selectedCaseFiltersItems = useMemo(() => {
        let items: any = [];
        try {
            if (caseFilters?.caseFilters?.length > 0 && Array.isArray(caseFilters?.caseFilters)) {
                caseFilters.caseFilters.forEach((caseFilter: any) => {
                    if (caseFilter?.conditionFilters?.length > 0 && Array.isArray(caseFilter?.conditionFilters)) {
                        caseFilter?.conditionFilters?.forEach((condition: any) => {
                            if (condition?.selectedFilters && Array.isArray(condition?.selectedFilters)) {
                                const filterLabel = condition?.filterSection?.label;
                                condition?.selectedFilters?.forEach((filter: any) => {
                                    items.push(`${filterLabel} - ${filter?.label}`);
                                });
                            }
                        });
                    }
                });
            }
            if (items?.length > 0) {
                items = [...new Set(items)];
            }
            return items;
        } catch (error) {
            console.log('error in selectedCaseFiltersItems', error);
            return [];
        }
    }, [caseFilters]);

    const caseFiltersLength = useMemo(() => {
        try {
            return selectedCaseFiltersItems?.length || 0;
        } catch (error) {
            console.log('error in caseFiltersLength', error);
            return 0;
        }
    }, [selectedCaseFiltersItems]);

    const containerHeight = useMemo(() => (
        caseFiltersLength > 0 ? 'calc(100% - 96px)' : 'calc(100% - 46px)'
    ), [caseFiltersLength]);

    const tableHeight = useMemo(() => (
        chartDimensions.height ? chartDimensions.height * 1.05 : 360
    ), [chartDimensions.height]);

    const caseFilterChipItems = useMemo(() => (
        (selectedCaseFiltersItems?.length > 0 && Array.isArray(selectedCaseFiltersItems))
            ? selectedCaseFiltersItems?.map((item: any, index: number) => (
                item ? <Chip key={`${index}-${item}`} label={item} color="primary" variant="outlined" size="small" /> : null
            )) : null
    ), [selectedCaseFiltersItems]);

    const toggleDrawer = (isOpen: boolean) => {
        setIsDrawerOpen(isOpen);
    };

    const handleDownload = (type: string) => {
        console.log(type);
    };

    const handleApplyCaseFilters = (filters: any) => {
        setCaseFilters(filters);
        toggleDrawer(false);
        if (props && props?.handleSaveDataToFilter) {
            props?.handleSaveDataToFilter({
                widgetId: props?.widgetId,
                value: {
                    caseFilters: filters
                }
            });
        }
    };

    const transformCaseFiltersToApiParams = (caseFiltersData: any) => {
        try {
            if (!caseFiltersData || !caseFiltersData.caseFilters || !Array.isArray(caseFiltersData.caseFilters)) {
                return '0';
            }

            const apiConditions = caseFiltersData.caseFilters.map((caseFilter: any, index: number) => {
                const conditionFilters = caseFilter?.conditionFilters?.map((condition: any) => {
                    let pFilterId = condition?.selectedFilters
                        ?.map((filter: any) => (filter && typeof filter.id === 'string' ? filter.id : NaN));

                    const pType = condition && typeof condition?.filterType === 'string' ? condition?.filterType : NaN;

                    if (pFilterId && pFilterId?.length > 0) {
                        try {
                            if (Array.isArray(pFilterId)) {
                                pFilterId = pFilterId.map((item: any) => {
                                    if (item) {
                                        if (typeof item === 'string' && item.split(',').length > 1) {
                                            return item.split(',').map((innerItem: any) => innerItem);
                                        }
                                        return item;
                                    }
                                    return null;
                                }).filter((item: any) => item !== null).flat(3);
                            }
                        } catch (error) {
                            console.log('error in transformCaseFiltersToApiParams', error);
                        }
                        return {
                            pType: pType,
                            pFilterId: pFilterId,
                        };
                    }
                    return null;
                }).filter((cf: any) => cf !== null);

                let condition: any = {};
                condition.operator = caseFilter?.condition || 'AND';
                return {
                    ...condition,
                    conditions: conditionFilters || [],
                    name: `case ${index + 1}`,
                };
            }).filter((c: any) => c?.conditions?.length > 0);

            if (apiConditions?.length === 0) {
                return '0';
            }
            let filter: any = {};
            return {
                filter: {
                    ...filter,
                    operator: caseFiltersData?.caseCondition,
                    conditions: apiConditions,
                },
            };
        } catch (error) {
            console.log('error in transformCaseFiltersToApiParams', error);
            return '0';
        }
    };

    const buildFilterJson = useCallback((filters: any) => {
        if (filters?.caseFilters?.length > 0 && Array.isArray(filters?.caseFilters)) {
            let filter: any = transformCaseFiltersToApiParams(filters);
            if (typeof filter === 'object') {
                filter = JSON.stringify(filter);
            }
            return filter;
        }
        return '0';
    }, []);

    const fetchWidgetData = useCallback(async () => {
        if (loadingRef.current) {
            console.log('Widget data fetch already in progress, skipping this cycle');
            return;
        }
        loadingRef.current = true;
        try {
            const FilterJson = buildFilterJson(caseFilters);
            const response = await getPredictiveDialerWidget({ FilterJson, TimeFilter: timeFilter });
            if (response && Array.isArray(response)) {
                setRowData(response.map(normalizeRow));
            } else {
                setRowData([]);
            }
        } catch (error) {
            console.error('Error fetching predictive dialer widget data:', error);
        } finally {
            loadingRef.current = false;
            setLastFetchedTime(getLastFetchedDetailFromBrowser());
        }
    }, [buildFilterJson, caseFilters, timeFilter]);

    useEffect(() => {
        fetchWidgetData();
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(fetchWidgetData, WIDGET_REFRESH_INTERVAL.PREDICTIVE_DIALER_WIDGET);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [fetchWidgetData]);

    const currentTimeLabel = useMemo(() => {
        const match = TIME_FILTERS.find((item) => item.value === timeFilter);
        return match?.label || TIME_FILTERS[0].label;
    }, [timeFilter]);

    const handleTimeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setTimeMenuAnchorEl(event.currentTarget);
    };

    const handleTimeMenuClose = () => {
        setTimeMenuAnchorEl(null);
    };

    const handleSelectTimeFilter = (value: string) => {
        setTimeFilter(value);
        handleTimeMenuClose();
        if (props && props?.handleSaveDataToFilter) {
            props?.handleSaveDataToFilter({
                widgetId: props?.widgetId,
                value: {
                    timeFilter: value
                }
            });
        }
    };

    return (
        <Card className="customCard">
            <div className="card-header">
                <div className="card-title">
                    Predictive Dialer
                    <span className="last-fetched-time" style={{ marginLeft: '6px' }}>Last updated at {lastFetchedTime}</span>
                </div>
                <div className="card-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <WidgetDownload element={downloadContent} handleDownload={handleDownload} xlsx rowData={rowData} columns={columns} name={'Predictive Dialer'}></WidgetDownload>
                    <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleTimeMenuOpen}>
                        <Typography variant="caption" sx={{ fontWeight: 400, fontSize: '12px', color: '#536580' }}>
                            {currentTimeLabel}
                        </Typography>
                        <KeyboardArrowDownOutlinedIcon style={{ color: '#536580', fontSize: '12px' }} />
                    </div>
                    <Menu
                        anchorEl={timeMenuAnchorEl}
                        open={Boolean(timeMenuAnchorEl)}
                        onClose={handleTimeMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {TIME_FILTERS.map((item) => (
                            <MenuItem key={item.value} onClick={() => handleSelectTimeFilter(item.value)}>
                                {item.label}
                            </MenuItem>
                        ))}
                    </Menu>
                    <div className="card-icon" onClick={() => toggleDrawer(true)}>
                        <Badge badgeContent={caseFiltersLength} color="primary">
                            <FilterAltOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
                        </Badge>
                    </div>
                </div>
            </div>

            <FilterDrawerCaseWise
                isOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                applyFilters={handleApplyCaseFilters}
                existingCaseFilters={caseFilters}
                allowedFilters={[]}
                dynamicFilters={true}
                useTagFilters={true}
            />

            {caseFiltersLength > 0 && (
                <Box sx={{ display: 'flex', width: '100%', gap: '4px', overflowX: 'auto', px: 2, py: 1 }}>
                    {caseFilterChipItems}
                </Box>
            )}

            <Box ref={chartContainerRef} sx={{ height: containerHeight }}>
                <Grid container height="100%">
                    <Grid item xs={12} height="100%">
                        <OZTable columns={columns} rows={rowData} maxHeight={360} downloadContent={downloadContent} noMaxHeight={true} height={tableHeight} />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
};

export default PredictiveDialerWidget;
